import Tab from "@/shared/components/Tab";
import { useMobileScreen } from "@/shared/hooks/useMobileScreen";
import { Box, Flex, rem } from "@mantine/core";
import {
  KeyboardEvent,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import classes from "./TabList.module.css";

export type TabOption<T extends string> = {
  value: T;
  label: string;
  isAccessible?: boolean;
  titleTooltip?: string;
  adornment?: ReactNode;
  /** Ícone à esquerda do rótulo (abas do agente). */
  icon?: ReactNode;
  /** Contador inline ao lado do rótulo (aba do agente: itens configurados). */
  count?: number;
  /**
   * Nome acessível da aba. Use quando o `adornment` carrega informação (o
   * ponto de saúde do WhatsApp): o adorno fica fora da aba, então o leitor de
   * tela só o anuncia junto se o status estiver no nome dela.
   */
  ariaLabel?: string;
};

interface TabsListProps<T extends string> {
  options: TabOption<T>[];
  activeTab: T;
  setActiveTab: (value: T) => void;
}

const TabsList = <T extends string>({
  options,
  activeTab,
  setActiveTab,
}: TabsListProps<T>) => {
  const { isMobileScreen } = useMobileScreen();
  const containerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Record<string, HTMLDivElement | null>>({});
  // Posição/largura do indicador deslizante, medidas da aba ativa (larguras
  // variam por aba). Mantém o valor anterior se a aba ativa ainda não montou.
  const [indicator, setIndicator] = useState<{ left: number; width: number }>({
    left: 0,
    width: 0,
  });

  const optionsSignature = options.map((o) => o.value).join("|");

  useLayoutEffect(() => {
    const measure = () => {
      const el = tabRefs.current[activeTab];
      if (el) setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
    };
    measure();

    const container = containerRef.current;
    if (!container || typeof ResizeObserver === "undefined") return;
    // Re-mede quando o layout muda (resize, fontes carregando, aba entrando/saindo).
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, optionsSignature, isMobileScreen]);

  useEffect(() => {
    const scrollToActiveTab = (tabName: string) => {
      const tab = tabRefs.current[tabName];
      if (containerRef.current && tab && isMobileScreen) {
        const container = containerRef.current;

        const containerWidth = container.offsetWidth;
        const tabLeft = tab.offsetLeft;
        const tabWidth = tab.offsetWidth;

        const extraPadding = 20;
        const scrollPosition =
          tabLeft - containerWidth / 2 + tabWidth / 2 + extraPadding;

        container.scrollTo({
          left: scrollPosition,
          behavior: "smooth",
        });
      }
    };

    scrollToActiveTab(activeTab);
  }, [activeTab, isMobileScreen]);

  // Setas/Home/End movem o foco entre as abas (ativação manual: Enter/Espaço).
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!["ArrowRight", "ArrowLeft", "Home", "End"].includes(event.key)) return;
    const tabs = Array.from(
      containerRef.current?.querySelectorAll<HTMLElement>('[role="tab"]') ?? []
    );
    const current = tabs.indexOf(document.activeElement as HTMLElement);
    if (current === -1) return;
    event.preventDefault();
    const last = tabs.length - 1;
    const targets: Record<string, number> = {
      Home: 0,
      End: last,
      ArrowRight: current === last ? 0 : current + 1,
      ArrowLeft: current === 0 ? last : current - 1,
    };
    const next = targets[event.key];
    tabs[next]?.focus();
  };

  // Sem aba ativa entre as opções, a primeira recebe o foco do Tab.
  const hasActive = options.some((o) => o.value === activeTab);

  return (
    <Flex
      ref={containerRef}
      role="tablist"
      onKeyDown={handleKeyDown}
      h="fit-content"
      direction="row"
      gap={rem(20)}
      wrap="nowrap"
      style={{
        position: "relative",
        overflowX: "auto",
        overflowY: "hidden",
        whiteSpace: "nowrap",
        scrollbarWidth: "none",
        paddingBottom: "2px",
      }}>
      {options.map((option, index) => (
        <Box
          key={option.value}
          ref={(el) => {
            tabRefs.current[option.value] = el;
          }}
          style={{ position: "relative" }}>
          <Tab
            text={option.label}
            icon={option.icon}
            isActive={activeTab === option.value}
            tabIndex={
              activeTab === option.value || (!hasActive && index === 0) ? 0 : -1
            }
            onClick={() => setActiveTab(option.value)}
            isAccessible={option.isAccessible}
            titleTooltip={option.titleTooltip}
            count={option.count}
            ariaLabel={option.ariaLabel}
          />
          {option.adornment && (
            <Box
              // O status já está no nome da aba: não anuncia duas vezes.
              aria-hidden={option.ariaLabel ? true : undefined}
              style={{
                position: "absolute",
                top: rem(-2),
                right: rem(-10),
              }}>
              {option.adornment}
            </Box>
          )}
        </Box>
      ))}

      <Box style={{ minWidth: rem(40) }} />

      {/* Barra deslizante única sob a aba ativa (corre até ela ao trocar). */}
      <div
        className={classes.indicator}
        style={{
          width: indicator.width,
          transform: `translateX(${indicator.left}px)`,
        }}
      />
    </Flex>
  );
};

export default TabsList;
